import React, { useEffect } from 'react';
import { useMaintenanceStore } from '@/store/maintenance';
import { useToastStore } from '@/store/toast';

export default function MaintenanceOverlay() {
  const { isMaintenance, isChecking, retryService } = useMaintenanceStore();
  const { showToast } = useToastStore();

  // 점검 화면 노출 시 배경 페이지 스크롤 잠금 (스크롤바 제거)
  useEffect(() => {
    if (!isMaintenance) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMaintenance]);

  if (!isMaintenance) {
    return null;
  }

  const handleRetry = async () => {
    if (isChecking) return;

    const isRecovered = await retryService();
    if (isRecovered) {
      showToast('서비스가 정상화되었습니다. 페이지를 새로고침합니다.', 2000);
      setTimeout(() => {
        const url = new URL(window.location.href);
        if (url.searchParams.has('testMaintenance')) {
          url.searchParams.delete('testMaintenance');
          window.location.replace(url.toString());
        } else {
          window.location.reload();
        }
      }, 500);
    } else {
      showToast('아직 서비스 점검 중입니다. 잠시 후 다시 시도해 주세요.', 3000);
    }
  };

  return (
    <div
      role='alertdialog'
      aria-modal='true'
      aria-labelledby='maintenance-title'
      aria-describedby='maintenance-description'
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#ffffff',
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        userSelect: 'none',
        overflow: 'hidden',
        overscrollBehavior: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          maxWidth: '560px',
          width: '100%',
        }}
      >
        {/* 상단 핑크/레드 원형 느낌표 아이콘 */}
        <div
          style={{
            width: '76px',
            height: '76px',
            borderRadius: '50%',
            backgroundColor: '#FF4D6D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(255, 77, 109, 0.25)',
            flexShrink: 0,
          }}
        >
          <svg
            width='36'
            height='36'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
          >
            {/* 상단 바 */}
            <rect x='10.5' y='4' width='3' height='10' rx='1.5' fill='white' />
            {/* 하단 점 */}
            <circle cx='12' cy='18' r='1.75' fill='white' />
          </svg>
        </div>

        {/* 메인 타이틀 */}
        <h1
          id='maintenance-title'
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#191919',
            letterSpacing: '-0.3px',
            marginBottom: '8px',
            margin: '0 0 8px 0',
          }}
        >
          서비스 점검 중
        </h1>

        {/* 서브 타이틀 */}
        <p
          style={{
            fontSize: '16px',
            fontWeight: 500,
            color: '#4A4A52',
            letterSpacing: '-0.2px',
            marginBottom: '28px',
            margin: '0 0 28px 0',
          }}
        >
          안정적인 서비스를 위해 점검 중입니다.
        </p>

        {/* 중앙 연회색 라운드 안내 박스 */}
        <div
          id='maintenance-description'
          style={{
            width: '100%',
            backgroundColor: '#F7F8F9',
            border: '1px solid #EBECEF',
            borderRadius: '16px',
            padding: '24px 20px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '32px',
            boxSizing: 'border-box',
          }}
        >
          <p
            style={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#2A2A30',
              letterSpacing: '-0.2px',
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            조속한 점검을 위해 최선을 다하고 있으니 잠시 후 다시 이용해주세요.
          </p>
          <p
            style={{
              fontSize: '14px',
              color: '#6C6C74',
              letterSpacing: '-0.2px',
              margin: 0,
            }}
          >
            이용에 불편을 드려 죄송합니다.
          </p>
        </div>

        {/* 다시 시도 버튼 */}
        <button
          type='button'
          onClick={handleRetry}
          disabled={isChecking}
          style={{
            height: '48px',
            padding: '0 36px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '15px',
            border: 'none',
            outline: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            backgroundColor: isChecking ? '#CCCCCC' : '#0083C7',
            color: isChecking ? '#888888' : '#ffffff',
            cursor: isChecking ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          }}
        >
          {isChecking ? (
            <>
              <svg
                style={{
                  animation: 'spin 1s linear infinite',
                  height: '18px',
                  width: '18px',
                  color: 'white',
                }}
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle style={{ opacity: 0.25 }} cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path
                  style={{ opacity: 0.75 }}
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                />
              </svg>
              <span>확인 중...</span>
            </>
          ) : (
            <span>다시 시도</span>
          )}
        </button>
      </div>
    </div>
  );
}
